import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { EditableText } from "@/components/EditableText";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { content, admin } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden px-6 pb-28 pt-32 md:px-12"
    >
      <div className="mx-auto grid w-full max-w-[1400px] items-end gap-14 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-8 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#5C564D]"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#078930]" aria-hidden="true" />
            <EditableText path="hero.eyebrow" testId="hero-eyebrow-text" />
          </motion.p>
          <h1 className="font-serif leading-[0.86] tracking-tight text-[#1A1713]">
            <MaskedLine delay={0.2} className="text-[clamp(4.5rem,14vw,11.5rem)]">
              <EditableText path="hero.line1" testId="hero-title-line-1" />
            </MaskedLine>
            <MaskedLine delay={0.38} className="text-[clamp(4.5rem,14vw,11.5rem)] italic text-[#5C564D]">
              <EditableText path="hero.line2" testId="hero-title-line-2" />
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-md text-base leading-relaxed text-[#5C564D] md:text-lg"
          >
            <EditableText path="hero.subtitle" testId="hero-subtitle" multiline />
          </motion.p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative aspect-[3/4] overflow-hidden border border-[#EADDCF]">
            <motion.img
              src={content.hero.image}
              alt="Ethiopian coffee ceremony at Kal's Cafe"
              style={{ y: imgY }}
              className="h-full w-full scale-110 object-cover"
              data-testid="hero-image"
            />
          </div>
          <p className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#5C564D]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#DA121A]" aria-hidden="true" />
            <EditableText path="hero.image_caption" testId="hero-image-caption" />
          </p>
          {admin && (
            <EditableText
              path="hero.image"
              testId="hero-image-url"
              className="mt-1 block w-full break-all text-[10px] text-[#5C564D]/60"
            />
          )}
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 md:left-12"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4 text-[#5C564D]" />
        </motion.span>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#5C564D]">Scroll</span>
      </motion.div>
    </section>
  );
};

export default Hero;
